import { cva } from 'class-variance-authority';
import React from 'react';
import Icon from '../Icon/icon';
import Spinner from '../Spinner/spinner';
import { clsxMerge } from '../../util';
import { IconNames } from '../../util/types';
import { onMouseEventHandler } from '../../util/eventHandlers';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'inline';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  text?: string;
  label?: string;
  disabled?: boolean;
  className?: string;
  onClick: React.MouseEventHandler<HTMLButtonElement | HTMLInputElement>;
  iconName?: IconNames;
  leadingIcon?: boolean;
  trailingIcon?: boolean;
  iconIndicator?: boolean;
  stateful?: boolean;
}

const generalButtonStyles = [
  'preflight',
  // Need to specify the font because disabling preflight makes the button not inherit the font
  // Lato is defined as font-sans in our tailwind theme configuration
  // Check src/theme/theme.js
  '!ds-font-sans',
  'ds-rounded',
  'ds-text-base',
  'ds-font-semibold',
  'ds-border',
  'ds-transition',
  'ds-duration-100',
  'ds-text-nowrap',
  'focus-visible:ds-ring',
  'focus-visible:ds-ring-2',
  'focus-visible:ds-outline-none',
  'focus-visible:ds-ring-offset-2'
];

const disabledBtnStyles = [
  '!ds-bg-neutrals-lighter',
  '!ds-text-neutrals-dark',
  '!ds-border-neutrals-light',
  '!ds-cursor-not-allowed',
  '!hover:ds-bg-neutrals-lighter',
  '!hover:ds-border-neutrals-light',
  '!hover:ds-text-neutrals-dark',
  '!focus-visible:ds-ring-neutrals-black',
  '!focus-visible:ds-border-neutrals-light'
];

const disabledInlineBtnStyles = [
  ...generalButtonStyles,
  'ds-p-1',
  '!ds-bg-neutrals-lighter',
  '!ds-text-neutrals-dark',
  '!ds-cursor-not-allowed',
  'ds-border-neutrals-lighter',
  'focus:ds-border-neutrals-light',
  'focus-visible:ds-ring-neutrals-black',
  '!focus-visible:ds-border-neutrals-light',
  'active:ds-border-neutrals-lighter'
];

export const button = cva(generalButtonStyles, {
  variants: {
    variant: {
      primary: [
        'ds-py-3',
        'ds-px-4',
        'ds-text-neutrals-white',
        'ds-bg-acuity-blue',
        'ds-border-acuity-blue',
        'hover:ds-bg-acuity-blue-dark',
        'hover:ds-border-acuity-blue-dark',
        'active:ds-bg-acuity-blue-darkest',
        'active:ds-border-acuity-blue-darkest',
        'active:ds-text-neutrals-white',
        'focus-visible:ds-text-neutrals-black',
        'focus-visible:ds-bg-acuity-blue-light',
        'focus-visible:ds-border-acuity-blue'
      ],
      secondary: [
        'ds-py-3',
        'ds-px-4',
        'ds-text-neutrals-black',
        'ds-bg-neutrals-white',
        'ds-border-neutrals',
        'hover:ds-bg-neutrals-lighter',
        'hover:ds-border-neutrals-darker',
        'active:ds-bg-neutrals-dark',
        'active:ds-border-neutrals-dark',
        'active:ds-text-neutrals-white',
        'focus-visible:ds-text-neutrals-black',
        'focus-visible:ds-bg-neutrals-lighter',
        'focus-visible:ds-border-neutrals-black',
        'focus-visible:ds-ring-neutrals-black'
      ],
      danger: [
        'ds-py-3',
        'ds-px-4',
        'ds-text-neutrals-white',
        'ds-bg-danger-red',
        'ds-border-danger-red',
        'hover:ds-bg-danger-red-dark',
        'hover:ds-border-danger-red-dark',
        'active:ds-bg-danger-red-darkest',
        'active:ds-border-danger-red-darkest',
        'active:ds-text-neutrals-white',
        'focus-visible:ds-text-neutrals-black',
        'focus-visible:ds-bg-danger-red-lighter',
        'focus-visible:ds-border-danger-red-darkest'
      ],
      inline: [
        'ds-p-1',
        'ds-text-neutrals-black',
        'ds-bg-neutrals-white',
        'ds-border-neutrals-white',
        'hover:ds-bg-neutrals-lighter',
        'hover:ds-border-neutrals-lighter',
        'active:ds-bg-neutrals-dark',
        'active:ds-border-neutrals-dark',
        'active:ds-text-neutrals-white',
        'focus-visible:ds-text-neutrals-black',
        'focus-visible:ds-bg-neutrals-lighter',
        'focus-visible:ds-border-neutrals-dark',
        'focus-visible:ds-ring-neutrals-black'
      ]
    },
    disabled: {
      true: disabledBtnStyles,
      false: []
    }
  },
  compoundVariants: [
    {
      variant: 'primary',
      disabled: false,
      class: 'focus-visible:ds-ring-acuity-blue'
    },
    {
      variant: 'danger',
      disabled: false,
      class: 'focus-visible:ds-ring-danger-red-darkest'
    },
    {
      variant: ['primary', 'danger'],
      disabled: true,
      class: 'focus-visible:ds-ring-neutrals-black'
    }
  ]
});

const getButtonClasses = (
  variant: ButtonVariant,
  disabled: boolean,
  className: string
) => {
  if (variant === 'inline' && disabled === true) {
    return clsxMerge(disabledInlineBtnStyles);
  }
  return clsxMerge(button({ variant, disabled }), className);
};

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  text,
  label = '',
  disabled = false,
  className = '',
  onClick,
  iconName,
  iconIndicator = false,
  leadingIcon = false,
  trailingIcon = false,
  stateful = false,
  ...props
}) => {
  const isDisabled = stateful || disabled;
  return (
    <>
      <button
        aria-disabled={isDisabled}
        aria-pressed={stateful}
        onClick={(e) => onMouseEventHandler(e, isDisabled, onClick)}
        className={getButtonClasses(variant, isDisabled, className)}
        aria-label={label}
        aria-live={stateful ? 'assertive' : 'off'}
        {...props}
      >
        <div className="ds-flex ds-flex-wrap ds-content-center ds-justify-center">
          {leadingIcon &&
            (stateful ? (
              <Spinner
                size="small"
                className="ds-mr-2 ds-self-center"
                focusable={false}
                label={label}
                ariaLive="off"
              />
            ) : (
              <Icon
                size="small"
                name={iconName ?? 'edit'}
                hasIndicator={iconIndicator}
                className="ds-mr-2 ds-self-center"
                altText=""
              />
            ))}
          <span>{text}</span>
          {trailingIcon &&
            (stateful ? (
              <Spinner
                size="small"
                className="ds-ml-2 ds-self-center"
                focusable={false}
                ariaLive="off"
                label={label}
              />
            ) : (
              <Icon
                size="small"
                name={iconName ?? 'edit'}
                hasIndicator={iconIndicator}
                className="ds-ml-2 ds-self-center"
                altText=""
              />
            ))}
        </div>
      </button>
    </>
  );
};

export default Button;
